import React, { Fragment, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Icon } from '@solarwindscloud/nighthawk-ui';
import { ErrorLabel } from '../';
import styles from './InputBox.module.scss';
import classNames from 'classnames';

const InputBox = React.forwardRef(
  (
    {
      name,
      label,
      icon,
      value,
      defaultValue,
      onChange,
      onBlur,
      errorMessage,
      placeholder,
      required,
      disabled,
      autoFocus,
      type,
      rightIconName,
      rightIconClassname,
      onClickRightIcon,
      dataTestString,
    },
    ref
  ) => {
    const [showError, setShowError] = useState(false);
    const [validated, setValidated] = useState(false);

    const handleChange = useCallback(
      (event, data) => {
        setValidated(false);
        if (onChange) onChange(data.value);
      },
      [onChange]
    );

    const handleBlur = useCallback(() => {
      if (onBlur) onBlur();
      setValidated(true);
    }, [onBlur]);

    useEffect(() => {
      setShowError(validated && !!errorMessage);
    }, [errorMessage, validated]);

    return (
      <Fragment>
        <Form.Field error={showError} required={required}>
          <label data-test-id="inputBox-label">{label}</label>
          <div className={styles.inputContainer}>
            <Input
              icon={icon}
              iconPosition="left"
              name={name}
              aria-label={label}
              value={value}
              placeholder={placeholder}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={disabled}
              autoFocus={autoFocus}
              type={type}
              ref={ref}
              defaultValue={defaultValue}
              data-test-id={dataTestString || 'inputBox-input'}
            />
            {rightIconName && (
              <Icon
                name={rightIconName}
                className={classNames(styles.rightIcon, rightIconClassname)}
                data-test-right-icon
                onClick={onClickRightIcon}
                data-test-id="inputBox-right-icon"
              />
            )}
          </div>
        </Form.Field>
        {showError && <ErrorLabel data-test-error-label>{errorMessage}</ErrorLabel>}
      </Fragment>
    );
  }
);

const propTypes = {
  label: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  required: PropTypes.bool,
  icon: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  type: PropTypes.string,
  rightIconName: PropTypes.string,
  rightIconClassname: PropTypes.string,
  onClickRightIcon: PropTypes.func,
  defaultValue: PropTypes.string,
  dataTestString: PropTypes.string,
};

InputBox.displayName = 'InputBox';
InputBox.propTypes = propTypes;
export default InputBox;
